import { useLayoutEffect, useRef, useState, useEffect } from 'react'

import { track } from 'utils/index'
import {
  amountValue,
  bankAccountValue,
  creditCardValue,
  frequencyValue
} from 'modules/contribution/LocalState'
import {
  getPaymentMethod,
  setPaymentMethod
} from 'modules/contribution/helpers'
import { SelectPaymentMethodList } from 'modules/contribution/components/SelectPaymentMethodList'

import addButton from 'static/img/add-button.svg'
import styles from './GiftingSelectPaymentMethod.module.scss'

const CURRENT_STEP = 'select-payment-method-new'

const GiftingSelectPaymentMethod = ({
  dispatch,
  user,
  refetchCurrentUser,
  onSelect,
  updatingPaymentMethod
}: Props) => {
  const eventName = 'GiftingSelectPaymentMethod'

  const amount = amountValue()
  const frequency = frequencyValue()

  const paymentMethod = getPaymentMethod(
    user,
    bankAccountValue,
    creditCardValue,
    updatingPaymentMethod
  )
  const { bankAccount, creditCard } = paymentMethod
  const [bankAccountId, setBankAccountId] = useState(
    bankAccount ? bankAccount.id : null
  )
  const [creditCardId, setCreditCardId] = useState(
    creditCard ? creditCard.id : null
  )

  const firstUpdate = useRef(true)

  useLayoutEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false
      return
    }

    let bankAccount
    let creditCard

    if (bankAccountId) {
      bankAccount = bankAccounts.find((ba: any) => ba.id === bankAccountId)
      creditCard = null
    } else if (creditCardId) {
      creditCard = creditCards.find((card: any) => card.id === creditCardId)
      bankAccount = null
    }

    track(eventName, { step: CURRENT_STEP })

    setPaymentMethod({
      bankAccount,
      creditCard,
      bankAccountValue: bankAccountValue,
      creditCardValue: creditCardValue
    })

    onSelect
      ? onSelect()
      : dispatch({
          type: 'setActiveStep',
          payload: {
            activeStep: 'confirmation'
          }
        })
  }, [bankAccountId, creditCardId])

  useEffect(() => {
    refetchCurrentUser()
  }, [])

  const { bankAccounts, creditCards } = user || {
    bankAccounts: [],
    creditCards: []
  }

  return (
    <div>
      <div className={styles.container}>
        <div className={styles.headerContainer}>
          <h2 className="typo-app-title-medium-adaptive">
            Select a payment method
          </h2>
        </div>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Select default</h2>
            <p>
              Choose your default payment method, connect your bank account, or
              add cards.
            </p>
          </div>
          <div>
            <SelectPaymentMethodList
              bankAccounts={bankAccounts}
              creditCards={creditCards}
              bankAccountId={bankAccountId}
              creditCardId={creditCardId}
              setCreditCardId={setCreditCardId}
              setBankAccountId={setBankAccountId}
              paymentMethod={paymentMethod}
              amount={amount}
              frequency={frequency}
            />
            <div className={styles.linkSection}>
              <button
                className={styles.link}
                type="button"
                onClick={() => {
                  track(eventName, {
                    step: CURRENT_STEP,
                    selectAdd: true
                  })

                  dispatch({
                    type: 'setActiveStep',
                    payload: {
                      activeStep: 'add-payment-method',
                      previousStep: 'select-payment-method'
                    }
                  })
                }}
              >
                <div className={styles.addBtn}>
                  <img src={addButton} alt="" className={styles.addImg} />
                </div>
                Add new payment method
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

interface Props {
  dispatch: any
  user: any
  refetchCurrentUser: () => Promise<any>
  onSelect?: () => void
  updatingPaymentMethod?: boolean
}

export default GiftingSelectPaymentMethod
